/**
 * Filtro por áreas dos "Últimos focos": campo de texto com sugestões
 * (combobox acessível) e até 3 áreas escolhidas, em chips.
 *
 * Leve: o índice de nomes (~100 KB) só é baixado quando o campo recebe foco;
 * a busca roda sobre ~2 mil nomes já normalizados (instantânea) e só com
 * texto digitado. A geometria da área é baixada ao escolher (ver
 * `fires/areaGeometry.ts`). O filtro vale para o app inteiro (mapa, contadores,
 * lista e exportação) — ver `fires/areaSync.ts`.
 */
import clsx from 'clsx';
import { AlertTriangle, Search, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { MAX_AREAS, useAreaFilterStore } from '../fires/areaFilter';
import { requestFitToAreas } from '../fires/areaSync';
import {
  areaId,
  areaKind,
  getLoadedAreaIndex,
  loadAreaIndex,
  searchAreas,
  type AreaEntry,
  type AreaIndex,
  type AreaRef,
} from '../fires/areaSearch';

type IndexState = { status: 'idle' | 'loading' | 'error'; index: null } | { status: 'ready'; index: AreaIndex };

function useAreaIndex(): [IndexState, () => void] {
  const [state, setState] = useState<IndexState>(() => {
    const index = getLoadedAreaIndex();
    return index ? { status: 'ready', index } : { status: 'idle', index: null };
  });
  const request = () => {
    if (state.status === 'ready' || state.status === 'loading') return;
    setState({ status: 'loading', index: null });
    loadAreaIndex().then(
      (index) => {
        setState({ status: 'ready', index });
        useAreaFilterStore.getState().fillNames();
      },
      () => setState({ status: 'error', index: null }),
    );
  };
  return [state, request];
}

function subtitle(a: Pick<AreaRef, 'layer' | 'detail'>): string {
  const kind = areaKind(a.layer).label;
  return a.detail && a.detail !== kind ? `${kind} · ${a.detail}` : kind;
}

function AreaChip(props: { area: AreaRef; missing: boolean; onRemove: () => void }) {
  const { area, missing, onRemove } = props;
  const name = area.name ?? area.key;
  return (
    <li
      className={clsx(
        'flex max-w-full min-w-0 items-center gap-1 rounded-full border py-0.5 pr-0.5 pl-2 text-xs',
        missing ? 'border-yellow-400/40 bg-yellow-400/10 text-yellow-200' : 'border-fogo-500/40 bg-fogo-500/10 text-ink-100',
      )}
      title={missing ? `${name}: área não encontrada nos dados` : `${name} — ${subtitle(area)}`}
    >
      {missing && <AlertTriangle className="size-3 shrink-0" aria-hidden />}
      <span className="min-w-0 truncate">
        {name}
        <span className="sr-only"> ({subtitle(area)})</span>
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remover filtro ${name}`}
        className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-ink-300 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:size-7"
      >
        <X className="size-3" aria-hidden />
      </button>
    </li>
  );
}

export function AreaFilter(props: { missing?: readonly string[] }) {
  const areas = useAreaFilterStore(useShallow((s) => s.areas));
  const addArea = useAreaFilterStore((s) => s.addArea);
  const removeArea = useAreaFilterStore((s) => s.removeArea);
  const clearAreas = useAreaFilterStore((s) => s.clearAreas);
  const [indexState, requestIndex] = useAreaIndex();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const id = useId();
  const full = areas.length >= MAX_AREAS;

  const selected = useMemo(() => new Set(areas.map(areaId)), [areas]);
  const suggestions: AreaEntry[] = useMemo(
    () => (indexState.status === 'ready' && text.trim() ? searchAreas(indexState.index, text, { exclude: selected }) : []),
    [indexState, text, selected],
  );
  const showList = open && !full && text.trim() !== '';

  useEffect(() => setActive(0), [text]);
  // Mantém a sugestão ativa visível ao navegar pelo teclado.
  useEffect(() => {
    if (showList) listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, showList]);

  const pick = (e: AreaEntry) => {
    // O mapa enquadra as áreas quando os limites carregarem.
    requestFitToAreas();
    addArea(e);
    setText('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown' && suggestions.length) {
      ev.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % suggestions.length);
    } else if (ev.key === 'ArrowUp' && suggestions.length) {
      ev.preventDefault();
      setOpen(true);
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (ev.key === 'Enter') {
      const e = showList ? suggestions[active] : undefined;
      if (e) {
        ev.preventDefault();
        pick(e);
      }
    } else if (ev.key === 'Escape' && (showList || text)) {
      // Fecha só as sugestões (não a folha em volta: ver `data-own-escape`).
      ev.preventDefault();
      if (showList) setOpen(false);
      else setText('');
    } else if (ev.key === 'Backspace' && !text && areas.length) {
      removeArea(areaId(areas[areas.length - 1]!));
    }
  };

  let status: string | null = null;
  if (showList) {
    if (indexState.status === 'loading' || indexState.status === 'idle') status = 'Carregando nomes das áreas…';
    else if (indexState.status === 'error') status = 'Não foi possível carregar os nomes das áreas.';
    else if (!suggestions.length) status = 'Nenhuma área com esse nome.';
  }

  const listId = `${id}-lista`;
  return (
    <div className="relative border-b border-ink-700/70 px-3 py-2" data-testid="area-filter">
      <label htmlFor={`${id}-campo`} className="sr-only">
        Filtrar focos por área
      </label>
      <div
        className={clsx(
          'flex items-center gap-1.5 rounded-lg border bg-ink-850 px-2 focus-within:border-mata-400',
          full ? 'border-ink-700' : 'border-ink-600',
        )}
      >
        <Search className="size-3.5 shrink-0 text-ink-400" aria-hidden />
        <input
          ref={inputRef}
          id={`${id}-campo`}
          type="text"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && suggestions[active] ? `${id}-op-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          data-own-escape={showList || text ? '1' : undefined}
          disabled={full}
          value={text}
          placeholder={full ? `Limite de ${MAX_AREAS} áreas` : 'Filtrar por município, TI, UC…'}
          onFocus={() => {
            requestIndex();
            setOpen(true);
          }}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            requestIndex();
          }}
          onKeyDown={onKeyDown}
          className="h-8 min-w-0 flex-1 bg-transparent text-xs text-ink-100 placeholder:text-ink-400 focus:outline-none disabled:cursor-not-allowed pointer-coarse:h-10 pointer-coarse:text-base"
        />
        {text && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setText('')}
            aria-label="Limpar texto"
            className="inline-flex size-6 shrink-0 items-center justify-center rounded text-ink-400 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        )}
      </div>

      {showList && (
        <div className="absolute inset-x-3 top-full z-30 -mt-1 overflow-hidden rounded-lg border border-ink-600 bg-ink-900 shadow-2xl">
          {status ? (
            <p className="px-3 py-2 text-xs text-ink-300" role="status">
              {status}
            </p>
          ) : null}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Áreas encontradas"
            className="scroll-thin max-h-64 overflow-y-auto overscroll-contain"
          >
            {suggestions.map((e, i) => (
              <li
                key={areaId(e)}
                id={`${id}-op-${i}`}
                data-index={i}
                role="option"
                aria-selected={i === active}
                // mousedown: escolhe antes do blur do campo fechar a lista.
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  pick(e);
                }}
                onMouseMove={() => i !== active && setActive(i)}
                className={clsx(
                  'cursor-pointer px-3 py-1.5 pointer-coarse:py-2.5',
                  i === active ? 'bg-ink-700/80' : 'hover:bg-ink-800',
                )}
              >
                <span className="block truncate text-xs font-medium text-white">{e.name}</span>
                <span className="block truncate text-[11px] text-ink-400">{subtitle(e)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {areas.length > 0 && (
        <div className="mt-2 flex items-start gap-1.5">
          <ul className="flex min-w-0 flex-1 flex-wrap gap-1" aria-label="Áreas do filtro">
            {areas.map((a) => (
              <AreaChip
                key={areaId(a)}
                area={a}
                missing={props.missing?.includes(areaId(a)) ?? false}
                onRemove={() => removeArea(areaId(a))}
              />
            ))}
          </ul>
          <button
            type="button"
            onClick={clearAreas}
            className="shrink-0 rounded px-1 py-0.5 text-[11px] text-ink-300 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400"
          >
            Limpar
          </button>
        </div>
      )}
    </div>
  );
}
